import React, { useMemo, useState } from "react";
import { Button } from "react-bootstrap";
import { WorkingHoursModal, WorkingHoursWeekValueDto } from "@me-pos/schedule-modal";

import { formatWeekValueSummaryItems, hasAnyWorkingHours, normalizeWeekValue } from "./helpers/workingHoursHelpers";
import { useTranslation } from "react-i18next";

interface WorkingHoursPickerFieldProps {
    modalTitle: string;
    timeZone: string;
    value: WorkingHoursWeekValueDto | null;
    onChange: (value: WorkingHoursWeekValueDto) => void;
    errorText?: string;
    isInvalid?: boolean;
    disabled?: boolean;
}

export const WorkingHoursPickerField: React.FC<WorkingHoursPickerFieldProps> = ({
    modalTitle,
    timeZone,
    value,
    onChange,
    errorText,
    isInvalid = false,
    disabled = false,
}) => {
    const { t, i18n } = useTranslation();
    const locale = i18n.language;
    const [isModalOpen, setIsModalOpen] = useState(false);
    const hasValue = hasAnyWorkingHours(value);
    const buttonText = hasValue ? "Change Hours" : "Select hours";

    const summaryItems = useMemo(() => {
        return formatWeekValueSummaryItems(value, locale, timeZone);
    }, [value, locale, timeZone]);

    const handleOpenModal = () => {
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
    };

    const handleSave = (newValue: WorkingHoursWeekValueDto) => {
        onChange(normalizeWeekValue(newValue));
        setIsModalOpen(false);
    };

    return (
        <>
            <div
                className={`d-flex align-items-start ${hasValue ? "gap-6 justify-content-between" : ""}`}
                style={{ maxWidth: "340px" }}
            >
                <div className="d-flex flex-column">
                    {hasValue ? (
                        <div className="d-flex flex-column gap-1">
                            {summaryItems.map((item) => (
                                <div
                                    key={`${item.rangeLabel}-${item.periods.join("|")}`}
                                    className="d-flex gap-1 fs-16"
                                >
                                    <div className="text-end text-nowrap">{item.rangeLabel} —</div>
                                    <div className="d-flex flex-column align-items-end">
                                        {item.periods.map((period, index) => (
                                            <div key={`${period}-${index}`} className="text-end fs-16 text-nowrap">
                                                {period}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : null}

                    {isInvalid && errorText ? <div className="text-danger small mt-1">{errorText}</div> : null}
                </div>
                <Button
                    variant="outline-primary"
                    className="py-1 px-3 text-nowrap"
                    style={{ height: "32px" }}
                    onClick={handleOpenModal}
                    disabled={disabled}
                >
                    {t(buttonText)}
                </Button>
            </div>

            {isModalOpen ? (
                <WorkingHoursModal
                    i18n={i18n}
                    isOpen={isModalOpen}
                    timeZone={timeZone}
                    title={modalTitle}
                    initialValue={value}
                    onClose={handleCloseModal}
                    onSave={handleSave}
                />
            ) : null}
        </>
    );
};
